import { Variants, useReducedMotion } from 'framer-motion';
import { useEffect, useState, useRef, useCallback } from 'react';

// ============================================================================
// REDUCED MOTION SUPPORT
// ============================================================================

/**
 * Hook that returns whether animations should be disabled
 * based on user's prefers-reduced-motion setting
 */
export const useAnimationEnabled = (): boolean => {
  const prefersReducedMotion = useReducedMotion();
  return !prefersReducedMotion;
};

// ============================================================================
// SPRING CONFIGURATIONS
// ============================================================================

export const springConfig = {
  gentle: { type: 'spring' as const, stiffness: 120, damping: 14 },
  snappy: { type: 'spring' as const, stiffness: 300, damping: 20 },
  bouncy: { type: 'spring' as const, stiffness: 400, damping: 10 },
  soft: { type: 'spring' as const, stiffness: 100, damping: 20 },
};

// ============================================================================
// DURATION PRESETS
// ============================================================================

export const durations = {
  instant: 0.1,
  fast: 0.15,
  normal: 0.25,
  slow: 0.4,
  pageTransition: 0.5,
};

// ============================================================================
// REUSABLE ANIMATION VARIANTS
// ============================================================================

/**
 * Fade in animation
 */
export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: durations.normal }
  },
  exit: {
    opacity: 0,
    transition: { duration: durations.fast }
  }
};

/**
 * Fade in with upward slide
 */
export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig.gentle
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: durations.fast }
  }
};

/**
 * Fade in with downward slide
 */
export const fadeInDown: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig.gentle
  },
  exit: {
    opacity: 0,
    y: 10,
    transition: { duration: durations.fast }
  }
};

/**
 * Scale animation (for buttons, cards on hover)
 */
export const scaleOnTap: Variants = {
  tap: { scale: 0.95 },
  hover: { scale: 1.02 }
};

/**
 * Pop in animation (scale from small)
 */
export const popIn: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springConfig.bouncy
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: { duration: durations.fast }
  }
};

/**
 * Slide from left
 */
export const slideFromLeft: Variants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig.gentle
  },
  exit: {
    opacity: 0,
    x: 30,
    transition: { duration: durations.fast }
  }
};

/**
 * Slide from right
 */
export const slideFromRight: Variants = {
  hidden: { opacity: 0, x: 30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig.gentle
  },
  exit: {
    opacity: 0,
    x: -30,
    transition: { duration: durations.fast }
  }
};

/**
 * Card hover effect
 */
export const cardHover: Variants = {
  rest: {
    scale: 1,
    y: 0,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  },
  hover: {
    scale: 1.02,
    y: -4,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    transition: springConfig.snappy
  },
  tap: {
    scale: 0.98,
    transition: { duration: durations.instant }
  }
};

/**
 * Button press animation
 */
export const buttonPress: Variants = {
  rest: { scale: 1 },
  hover: {
    scale: 1.03,
    transition: springConfig.snappy
  },
  tap: {
    scale: 0.95,
    transition: { duration: durations.instant }
  }
};

/**
 * Gentle float animation (continuous)
 */
export const float: Variants = {
  animate: {
    y: [0, -6, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }
};

/**
 * Pulse glow effect (for current week/year markers)
 */
export const pulseGlow: Variants = {
  animate: {
    scale: [1, 1.05, 1],
    opacity: [1, 0.8, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }
};

/**
 * Heartbeat animation
 */
export const heartbeat: Variants = {
  animate: {
    scale: [1, 1.15, 1, 1.1, 1],
    transition: {
      duration: 0.8,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }
};

// ============================================================================
// STAGGER UTILITIES
// ============================================================================

/**
 * Container variant for staggered children animations
 */
export const staggerContainer = (
  staggerDelay: number = 0.03,
  delayChildren: number = 0
): Variants => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggerDelay,
      delayChildren: delayChildren
    }
  },
  exit: {
    opacity: 0,
    transition: {
      staggerChildren: staggerDelay / 2,
      staggerDirection: -1
    }
  }
});

/**
 * Child item for staggered animations
 */
export const staggerChild: Variants = {
  hidden: { opacity: 0, y: 10, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: springConfig.gentle
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: durations.fast }
  }
};

/**
 * Grid cell animation for wave effect
 */
export const gridCell: Variants = {
  hidden: { opacity: 0, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springConfig.bouncy
  }
};

// ============================================================================
// VIEW TRANSITION VARIANTS
// ============================================================================

/**
 * Get slide direction based on view change
 */
export const getSlideDirection = (
  fromIndex: number,
  toIndex: number
): 'left' | 'right' => {
  return toIndex > fromIndex ? 'right' : 'left';
};

/**
 * Page transition variants with direction
 */
export const pageTransition = (direction: 'left' | 'right'): Variants => ({
  initial: {
    opacity: 0,
    x: direction === 'right' ? 50 : -50
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: durations.pageTransition,
      ease: [0.25, 0.1, 0.25, 1]
    }
  },
  exit: {
    opacity: 0,
    x: direction === 'right' ? -50 : 50,
    transition: {
      duration: durations.fast
    }
  }
});

// ============================================================================
// NUMBER ANIMATION HOOK
// ============================================================================

/**
 * Hook for animating numbers counting up
 */
export const useCountUp = (
  end: number,
  duration: number = 1500,
  delay: number = 0
): number => {
  const [count, setCount] = useState(0);
  const animationEnabled = useAnimationEnabled();
  const startTimeRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!animationEnabled) {
      setCount(end);
      return;
    }

    // Reset for new target
    setCount(0);
    startTimeRef.current = null;

    const startAnimation = () => {
      const animate = (currentTime: number) => {
        if (startTimeRef.current === null) {
          startTimeRef.current = currentTime;
        }

        const elapsed = currentTime - startTimeRef.current;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out cubic
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.floor(easeOut * end);

        setCount(currentValue);

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    const timeoutId = setTimeout(startAnimation, delay);

    return () => {
      clearTimeout(timeoutId);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [end, duration, delay, animationEnabled]);

  return count;
};

/**
 * Hook for animating floating point numbers
 */
export const useCountUpFloat = (
  end: number,
  decimals: number = 2,
  duration: number = 1500,
  delay: number = 0
): string => {
  const count = useCountUp(Math.round(end * Math.pow(10, decimals)), duration, delay);
  return (count / Math.pow(10, decimals)).toFixed(decimals);
};

// ============================================================================
// CELEBRATION/CONFETTI UTILITIES
// ============================================================================

export interface ConfettiParticle {
  id: number;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  color: string;
  shape: 'circle' | 'square' | 'heart' | 'star';
  velocityX: number;
  velocityY: number;
  rotationSpeed: number;
}

const confettiColors = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#ec4899', // pink
];

const shapes: ConfettiParticle['shape'][] = ['circle', 'square', 'heart', 'star'];

/**
 * Generate confetti particles
 */
export const generateConfetti = (count: number = 50): ConfettiParticle[] => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: -10 - Math.random() * 20,
    rotation: Math.random() * 360,
    scale: 0.5 + Math.random() * 0.5,
    color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
    shape: shapes[Math.floor(Math.random() * shapes.length)],
    velocityX: (Math.random() - 0.5) * 4,
    velocityY: 2 + Math.random() * 3,
    rotationSpeed: (Math.random() - 0.5) * 10
  }));
};

// ============================================================================
// MILESTONE DETECTION
// ============================================================================

export interface Milestone {
  type: 'birthday' | 'round_days' | 'round_weeks' | 'season_complete' | 'first_visit';
  value?: number;
  message: string;
}

/**
 * Check if current stats trigger a celebration milestone
 */
export const checkMilestones = (
  daysLived: number,
  weeksLived: number,
  currentSeason: string,
  previousSeason: string | null,
  isFirstVisit: boolean
): Milestone | null => {
  // First visit celebration
  if (isFirstVisit) {
    return {
      type: 'first_visit',
      message: 'Welcome to your life journey!'
    };
  }

  // Round day milestones (10000, 20000, etc)
  const roundDays = [1000, 5000, 10000, 15000, 20000, 25000, 30000];
  for (const milestone of roundDays) {
    if (Math.floor(daysLived) === milestone) {
      return {
        type: 'round_days',
        value: milestone,
        message: `${milestone.toLocaleString()} days lived!`
      };
    }
  }

  // Round week milestones
  const roundWeeks = [500, 1000, 1500, 2000, 2500, 3000];
  for (const milestone of roundWeeks) {
    if (Math.floor(weeksLived) === milestone) {
      return {
        type: 'round_weeks',
        value: milestone,
        message: `${milestone.toLocaleString()} weeks of life!`
      };
    }
  }

  // Season transition
  if (previousSeason && currentSeason !== previousSeason) {
    return {
      type: 'season_complete',
      message: `Entering the ${currentSeason} of your life!`
    };
  }

  return null;
};

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to detect first visit using localStorage
 */
export const useFirstVisit = (): [boolean, () => void] => {
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  useEffect(() => {
    const visited = localStorage.getItem('mortalmath_visited');
    if (!visited) {
      setIsFirstVisit(true);
    }
  }, []);

  const markVisited = useCallback(() => {
    localStorage.setItem('mortalmath_visited', 'true');
    setIsFirstVisit(false);
  }, []);

  return [isFirstVisit, markVisited];
};

/**
 * Hook to track previous value for comparisons
 */
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};
